import { useRef, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { useToast } from "@/hooks/use-toast";

// Lazy import for Capacitor plugin to prevent iOS freeze
let CapacitorSpeechRecognition: any = null;

const getCapacitorSpeechRecognition = async () => {
  if (!CapacitorSpeechRecognition && Capacitor.isNativePlatform()) {
    try {
      const module = await import('@capacitor-community/speech-recognition');
      CapacitorSpeechRecognition = module.SpeechRecognition;
    } catch (e) {
      console.error('Failed to load speech recognition module:', e);
    }
  }
  return CapacitorSpeechRecognition;
};

export interface NativeSpeechRecognitionProps {
  onTranscriptionComplete: (text: string) => void;
  language: string;
  isListening: boolean;
  setIsListening: (isListening: boolean) => void;
}

export const useNativeSpeechRecognition = ({
  onTranscriptionComplete,
  language,
  isListening,
  setIsListening
}: NativeSpeechRecognitionProps) => {
  const { toast } = useToast();
  const listenerActiveRef = useRef(false);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      if (listenerActiveRef.current && CapacitorSpeechRecognition) {
        try {
          CapacitorSpeechRecognition.removeAllListeners();
          CapacitorSpeechRecognition.stop();
        } catch (e) {
          console.log('Native speech cleanup error:', e);
        }
      }
    };
  }, []);

  const isSupported = async () => {
    if (!Capacitor.isNativePlatform()) return false;
    
    try {
      const SpeechPlugin = await getCapacitorSpeechRecognition();
      if (!SpeechPlugin) return false;
      
      const result = await Promise.race([
        SpeechPlugin.available(),
        new Promise<{ available: boolean }>((_, reject) => 
          setTimeout(() => reject(new Error('Timeout')), 2000)
        )
      ]);
      return result.available;
    } catch (error) {
      console.error('Error checking speech recognition availability:', error);
      return true; // Assume available on iOS
    }
  };

  const startRecognition = async () => {
    try {
      const SpeechPlugin = await getCapacitorSpeechRecognition();
      if (!SpeechPlugin) {
        toast({
          title: "Not Available",
          description: "Speech recognition is not available.",
          variant: "destructive",
        });
        return;
      }

      // Clean up any existing listeners
      if (listenerActiveRef.current) {
        try {
          SpeechPlugin.removeAllListeners();
          await new Promise(resolve => setTimeout(resolve, 50));
          SpeechPlugin.stop();
          await new Promise(resolve => setTimeout(resolve, 50));
        } catch (e) {
          console.log('Pre-cleanup:', e);
        }
        listenerActiveRef.current = false;
      }

      // Check availability
      let available = true;
      try {
        const result = await Promise.race([
          SpeechPlugin.available(),
          new Promise<{ available: boolean }>((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 2000)
          )
        ]);
        available = result.available;
      } catch (e) {
        console.log('Availability check timed out:', e);
      }

      if (!available) {
        toast({
          title: "Not Supported",
          description: "Speech recognition is not available on your device.",
          variant: "destructive",
        });
        return;
      }

      // Request permissions
      let permResult: any = { speechRecognition: 'granted' };
      try {
        permResult = await Promise.race([
          SpeechPlugin.requestPermissions(),
          new Promise<any>((_, reject) => 
            setTimeout(() => reject(new Error('Timeout')), 3000)
          )
        ]);
        console.log('iOS speech permission result:', permResult);
      } catch (e) {
        console.log('Permission request timed out:', e);
      }
      
      if (permResult.speechRecognition === 'denied') {
        toast({
          title: "Permission Required",
          description: "Please enable Speech Recognition in Settings.",
          variant: "destructive",
        });
        return;
      }

      // Set up listeners
      listenerActiveRef.current = true;

      // If start() hangs on iOS, listeningState will still confirm whether it started.
      let resolveStarted: ((started: boolean) => void) | null = null;
      const startedPromise = new Promise<boolean>((resolve) => {
        resolveStarted = resolve;
      });
      const startedTimeout = window.setTimeout(() => resolveStarted?.(false), 3500);

      try {
        await SpeechPlugin.addListener('listeningState', (data: { status: 'started' | 'stopped' }) => {
          console.log('iOS: listeningState:', data);
          if (data.status === 'started') resolveStarted?.(true);
          if (data.status === 'stopped') setIsListening(false);
        });
      } catch (e) {
        console.log('ListeningState listener error:', e);
      }

      await SpeechPlugin.addListener('partialResults', (data: { matches: string[] }) => {
        if (data.matches && data.matches.length > 0) {
          onTranscriptionComplete(data.matches[0]);
        }
      });

      // Small delay before starting
      await new Promise(resolve => setTimeout(resolve, 50));

      // Convert language format for iOS (en-US -> en_US)
      const iosLanguage = language.replace('-', '_');

      const startPromise = SpeechPlugin.start({
        language: iosLanguage,
        partialResults: true,
        popup: false,
      });

      const started = await Promise.race([
        startPromise.then(() => true).catch((e: any) => {
          console.log('Start rejected:', e);
          return false;
        }),
        startedPromise,
        new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), 4500)),
      ]);

      window.clearTimeout(startedTimeout);

      if (!started) {
        listenerActiveRef.current = false;
        try {
          await SpeechPlugin.removeAllListeners();
        } catch {}
        try {
          await SpeechPlugin.stop();
        } catch {}

        toast({
          title: "Error",
          description: "Failed to start speech recognition.",
          variant: "destructive",
        });
        setIsListening(false);
        return;
      }

      setIsListening(true);

      toast({
        title: "Recording started",
        description: "Speak now...",
      });
    } catch (error: any) {
      console.error('Error with native speech recognition:', error);
      listenerActiveRef.current = false;
      
      const SpeechPlugin = await getCapacitorSpeechRecognition();
      if (SpeechPlugin) {
        try {
          SpeechPlugin.removeAllListeners();
          SpeechPlugin.stop();
        } catch (e) {}
      }
      
      toast({
        title: "Error",
        description: error?.message || "Failed to start speech recognition.",
        variant: "destructive",
      });
      setIsListening(false);
    }
  };

  const stopRecognition = async () => {
    listenerActiveRef.current = false;
    
    const SpeechPlugin = await getCapacitorSpeechRecognition();
    if (SpeechPlugin) {
      try {
        SpeechPlugin.removeAllListeners();
      } catch (e) {
        console.log('Remove listeners error:', e);
      }
      
      await new Promise(resolve => setTimeout(resolve, 50));
      
      try {
        SpeechPlugin.stop();
      } catch (error) {
        console.error('Error stopping native speech recognition:', error);
      }
    }
    
    setIsListening(false);
    toast({
      title: "Recording stopped",
      description: "Speech recognition has been stopped.",
    });
  };

  return {
    isSupported,
    startRecognition,
    stopRecognition
  };
};

